import type { RegistryLookups, RoleDataField } from '@k-management/shared';
import { useCan } from '../../auth/AuthProvider.js';
import { Field } from '../../components/Field.js';
import { ReclassPicker } from '../../components/ReclassPicker.js';

const LABELS: Record<RoleDataField, string> = {
  termsCode: 'Termini di pagamento',
  methodCode: 'Metodo di pagamento',
  priorityCode: 'Priorità',
  reclassAccount: 'Conto riclassificato',
  costCenter: 'Centro di costo',
  preferredAccount: 'Conto preferenziale',
  iban: 'IBAN',
};

const PAYMENT: RoleDataField[] = [
  'termsCode',
  'methodCode',
  'priorityCode',
  'reclassAccount',
  'costCenter',
  'preferredAccount',
];

/** The data a role asks for («Dati richiesti» in Ruoli anagrafici), within what the user may see. */
export function RoleFields({
  fields,
  lookups,
  value,
  errors = {},
  disabled = false,
  onChange,
}: {
  fields: RoleDataField[];
  lookups: RegistryLookups;
  value: Partial<Record<RoleDataField, string | null>>;
  errors?: Record<string, string>;
  disabled?: boolean;
  onChange: (value: Partial<Record<RoleDataField, string>>) => void;
}) {
  const canPayment = useCan('anagrafica.payment-terms');
  const canBank = useCan('anagrafica.bank-details');
  const shown = fields.filter((field) =>
    PAYMENT.includes(field) ? canPayment : field !== 'iban' || canBank,
  );
  if (shown.length === 0) return null;

  const set = (field: RoleDataField, text: string) =>
    onChange({ ...value, [field]: text } as Partial<Record<RoleDataField, string>>);
  const list = (field: RoleDataField) =>
    ({
      termsCode: lookups.terms,
      methodCode: lookups.methods,
      priorityCode: lookups.priorities,
      reclassAccount: lookups.reclassAccounts,
      costCenter: lookups.costCenters,
      preferredAccount: lookups.accounts,
      iban: null,
    })[field];

  return (
    <div className="form-grid">
      {shown.map((field) => {
        const options = list(field);
        return (
          <Field key={field} label={LABELS[field]} error={errors[field]}>
            {field === 'reclassAccount' ? (
              <ReclassPicker
                value={value[field] ?? null}
                choices={lookups.reclassAccounts}
                disabled={disabled}
                onChange={(code) => set(field, code ?? '')}
              />
            ) : options ? (
              <select
                value={value[field] ?? ''}
                disabled={disabled}
                onChange={(e) => set(field, e.target.value)}
              >
                <option value="">—</option>
                {options.map((entry) => (
                  <option key={entry.code} value={entry.code}>
                    {field === 'priorityCode'
                      ? `${entry.code} · ${entry.description}`
                      : entry.description}
                  </option>
                ))}
                {/* Written by hand before the table existed: it stays until changed. */}
                {value[field] && !options.some((entry) => entry.code === value[field]) && (
                  <option value={value[field] ?? ''}>{value[field]} (non in tabella)</option>
                )}
              </select>
            ) : (
              <input
                className={field === 'iban' ? 'mono-input' : undefined}
                value={value[field] ?? ''}
                disabled={disabled}
                onChange={(e) => set(field, e.target.value)}
              />
            )}
          </Field>
        );
      })}
    </div>
  );
}

export const ROLE_FIELD_LABELS = LABELS;
